import { ChangeDetectionStrategy, Component, computed, inject, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import katex from 'katex';
import { BoundsBreakdown, calculateBounds, randomBoundsNumber } from './upper-lower-bounds.model';

const PINK = '#ec4899';
const TEAL = '#14b8a6';

@Component({
  selector: 'dojo-upper-lower-bounds',
  standalone: true,
  imports: [FormsModule, ButtonComponent, IconComponent],
  templateUrl: './upper-lower-bounds.component.html',
  styleUrl: './upper-lower-bounds.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'block' },
})
export class UpperLowerBoundsComponent {
  private readonly sanitizer = inject(DomSanitizer);

  readonly value = signal(2.6);
  readonly dp = signal(1);
  readonly inputText = signal('2.6');

  readonly bounds = computed<BoundsBreakdown>(() => calculateBounds(this.value(), this.dp()));

  private readonly valueDisplay = computed(() => this.value().toFixed(this.dp()));

  /** Step 1 – degree of accuracy formula. */
  readonly degreeHtml = computed(() => {
    const b = this.bounds();
    return this.renderKatex(`\\text{Degree of accuracy} = ${b.degreeOfAccuracy} \\implies \\text{Half} = ${b.half}`);
  });

  /** Diagram – left connector label "−half". */
  readonly minusHalfHtml = computed(() => {
    return this.renderKatex(`-\\,${this.bounds().half}`);
  });

  /** Diagram – right connector label "+half". */
  readonly plusHalfHtml = computed(() => {
    return this.renderKatex(`+\\,${this.bounds().half}`);
  });

  /** Diagram – center value. */
  readonly valueHtml = computed(() => {
    return this.renderKatex(this.valueDisplay());
  });

  /** Diagram – lower bound value (pink). */
  readonly lbValueHtml = computed(() => {
    return this.renderKatex(`\\textcolor{${PINK}}{${this.bounds().lowerBound}}`);
  });

  /** Diagram – upper bound value (teal). */
  readonly ubValueHtml = computed(() => {
    return this.renderKatex(`\\textcolor{${TEAL}}{${this.bounds().upperBound}}`);
  });

  /** Diagram – Lower Bound label. */
  readonly lbTagHtml = computed(() => this.renderKatex(`\\text{Lower Bound}`));

  /** Diagram – Upper Bound label. */
  readonly ubTagHtml = computed(() => this.renderKatex(`\\text{Upper Bound}`));

  /** Step 3 – inequality. */
  readonly answerHtml = computed(() => {
    const b = this.bounds();
    return this.renderKatex(`\\textcolor{${PINK}}{${b.lowerBound}} \\leq x < \\textcolor{${TEAL}}{${b.upperBound}}`);
  });

  onInputChange(text: string): void {
    this.inputText.set(text);
    const parsed = parseFloat(text);
    if (!isNaN(parsed) && isFinite(parsed)) {
      this.value.set(parsed);
    }
  }

  onDpChange(newDp: number): void {
    this.dp.set(Math.max(0, Math.min(6, newDp)));
  }

  randomize(): void {
    const { value, dp } = randomBoundsNumber();
    this.value.set(value);
    this.inputText.set(value.toString());
    this.dp.set(dp);
  }

  private renderKatex(latex: string) {
    return this.sanitizer.bypassSecurityTrustHtml(katex.renderToString(latex, { throwOnError: false, output: 'html' }));
  }
}
